<template>
  <div class="date-picker">
    <label>{{ label }}</label>
      <el-date-picker
        :class="{
          'pick start-date': pickingStartDate,
          'pick end-date': pickingEndDate
        }"
        v-model="modelProp"
        type="daterange"
        align="right"
        unlink-panels
        :range-separator="$t('date_picker.to')"
        :start-placeholder="$t('date_picker.start_date')"
        :end-placeholder="$t('date_picker.end_date')"
        :picker-options="datePickerOptions"
        :onPick="onPick"
        @focus="onFocus"
        :disabled="!enabledAction"
      >
      </el-date-picker>
  </div>
</template>

<script>
  import {
    path,
    head,
    last,
    defaultTo,
    always,
    compose
  } from 'ramda';
  import {
    subWeeks,
    subMonths,
    subYears,
    startOfMonth,
    startOfWeek,
    startOfYear,
    toDate
  } from 'date-fns'


  const now = () => toDate(new Date())

  export default {
    props: {
      label: {
        type: String,
        default: ''
      },
      value: {
        type: Object,
        default: () => ({
          start: startOfMonth(Date.now()),
          end: toDate(Date.now()),
        })
      },
      setMinDate: {
        type: Boolean,
        default: true
      },
      disabledDate: {
        type: Function,
        default: always(false)
      },
      shortcutsEnabled: {
        type: Boolean,
        default: false,
      },
      enabledAction: {
        type: Boolean,
        default: true
      }
    },
    data() {
      return {
        pickingStartDate: false,
        pickingEndDate: false,
        pickerOptions: {
          disabledDate: (time) => {
            return this.disabledDate(time)
          },
        },
        
      }
    },
    computed: {
      modelProp: {
        get () {
          const start = compose(
            defaultTo(subMonths(Date.now(), 1)),

            path(['value', 'start'])
          )(this)
          const end = compose(
            defaultTo(Date.now()),
            path(['value', 'end'])
          )(this)

          return [ start, end ]
        },
        set (value = []) {
          const dateIntervalObject = {
            start: head(defaultTo([], value)),
            end: last(defaultTo([], value)),
          }
          return this.$emit('input', dateIntervalObject)
        },
      },
      shortcuts() {
        return [
          {
          text: this.$t('date_interval_picker.this_month'),
          onClick: (picker) => {
            const start = startOfMonth(now())
            const end = now()
            // this.pickingStartDate = false
            // this.pickingEndDate = false

            picker.$emit('pick', [start, end])
          }
        }, {
          text: this.$t('date_picker.last_week'),
          onClick: (picker) => {
            const start = subWeeks(startOfWeek(now(), { weekStartsOn: 1 }), 1)
            const end = startOfWeek(now())
            // this.pickingStartDate = false
            // this.pickingEndDate = false

            picker.$emit('pick', [start, end])
          }
        }, {
          text: this.$t('date_picker.last_month'),
          onClick: (picker) => {
            const start = subMonths(startOfMonth(now()), 1)
            const end = startOfMonth(now()) - 1
            // this.pickingStartDate = false
            // this.pickingEndDate = false

            picker.$emit('pick', [start, end])
          }
        }, {
          text: this.$t('date_picker.last_three_months'),
          onClick: (picker) => {
            const start = subMonths(startOfMonth(now()), 3)
            const end = startOfMonth(now()) - 1
            // this.pickingStartDate = false
            // this.pickingEndDate = false

            picker.$emit('pick', [start, end])
          }
        }, {
          text: this.$t('date_picker.last_six_months'),
          onClick: (picker) => {
            const start = subMonths(startOfMonth(now()), 6)
            const end = startOfMonth(now()) - 1
            // this.pickingStartDate = false
            // this.pickingEndDate = false

            picker.$emit('pick', [start, end])
          }
        }, {
          text: this.$t('date_picker.last_year'),
          onClick: (picker) => {
            const start = subYears(startOfYear(now()), 1)
            const end = startOfYear(now()) - 1;
            // this.pickingStartDate = false
            // this.pickingEndDate = false

            picker.$emit('pick', [start, end])
          }
        }]
      },
      datePickerOptions() {
        if(this.shortcutsEnabled) {
          return {
            ...this.pickerOptions,
            shortcuts: this.shortcuts,
            onPick: this.onPick,
          }
        }
          return {
            ...this.pickerOptions,
            onPick: this.onPick,
          }
      }
    },
    methods: {
      onPick(dateInterval) {
        if(dateInterval.minDate && !dateInterval.maxDate) {
          // start date selected
          this.pickingStartDate = false
          this.pickingEndDate = true
        } else if (dateInterval.minDate && dateInterval.maxDate) {
          // end date selected
          this.pickingStartDate = false
          this.pickingEndDate = false
        }
      },
       onFocus() {
        this.pickingStartDate = true
      }
    }
  }
</script>

<style lang="scss" scoped>
  @import '../../scss/_colors.scss';
  .date-picker {
    flex: 1;
    max-width: 405px;
    label {
      color: $dark-blue-2;
      font-size: 18px;
      font-weight: 500;
      line-height: initial;
      margin-bottom: 8px;
      opacity: 1;
      text-transform: none;
      display: flex;
    }
    ::v-deep input {
      border-radius: 0;
      font-family: 'SF Pro Display';
      font-weight: 300;
      font-size: 14px;
      color: $darker-blue;
      height: 22px;
      cursor: pointer;
      &:focus {
        box-shadow: none;
        border-bottom: 2px solid $purple;
      }
    }
  }

  .el-range-editor.el-input__inner {
    border-radius: 8px;
    width: 100%;
    height: 52px;
  }

  ::v-deep .vc-day-content {
    font-family: 'SF Pro Display';
    font-weight: 300;
    font-size: 13px;
    color: $darker-blue;
  }
  ::v-deep .vc-title-layout {
    font-family: 'SF Pro Display';
    font-weight: 300;
    font-size: 16px;
    color: $gray;
  }

  ::v-deep span.el-range-separator {
    word-break: keep-all;
    width: fit-content;
    margin-top: 10px;
  }

  .pick.start-date::v-deep.el-date-editor.el-range-editor .el-range-input:first-of-type,
  .pick.end-date::v-deep.el-date-editor.el-range-editor .el-range-input:last-of-type {
    box-shadow: none;
    border-bottom: 2px solid #409FFF;
  }
</style>
